import { useToastError } from '~/utils/useToastError';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { validate } from '~/utils/validateAccmmd';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { InputBar, Button, Textarea, IconButton } from '~/ui';
import styles from './AccommodationForm.module.scss';
import { instance } from '~/utils/api/api';
import { useTranslation } from 'react-i18next';
import { useToastSuccess } from '~/utils/useToastSuccess';
import CustomSelect from '../Shared/CustomSelect/CustomSelect';
import { getTimezones } from '../UserForm/timezones';
import { Modal } from '../Shared/Modal/Modal';
import PhotoPickerContainer, {
  IPhoto,
} from '../AnnouncementForm/PhotoPickerContainer/PhotoPickerContainer';
import PhotoIcon from '~/assets/svg/newSvg/picture.svg?react';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import { getAccommodationsPhotos } from '~/utils/api/queries';
import { useDebounce } from '@uidotdev/usehooks';

export interface AccommodationItem {
  id: number;
  name: string;
  description: string;
  address: string;
  floors: string;
  rooms: string;
  beds: string;
  photos: IPhoto[];
  section: string;
  asset_id: number | null;
  full_name: string;
  assets: IAsset;
  utc: string;
}

export interface AccommodationValues {
  name: string;
  description: string;
  address: string;
  floors: string;
  rooms: string;
  beds: string;
  section: string;
  asset_id: number | null;
  assets: IAsset;
  photos: IPhoto[];
  timezone: {
    id: string;
    name: string;
  };
}

export interface IAsset {
  id: number;
  address: string;
  created_at: string;
  updated_at: string;
  name: string;
  inn: string;
  description: string;
  image_path: string;
  box_office: { id: number; name: string }[];
}

interface AccommodationFormProps {
  onSubmit: (values: AccommodationValues) => Promise<void>;
  accommodation?: AccommodationItem;
  storageKey?: string;
  isAssetEditAvailable?: boolean;
  isLoading?: boolean;
  hasAvailable?: boolean;
}

export const AccommodationForm = ({
  onSubmit,
  accommodation,
  storageKey,
  isAssetEditAvailable,
  isLoading,
  hasAvailable,
}: AccommodationFormProps): JSX.Element => {
  const [assets, setAssets] = useState<IAsset[]>([]);
  const { t } = useTranslation();
  const toastSuccess = useToastSuccess();
  const toastError = useToastError();
  const timezonesList = getTimezones();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState<number>(1);

  const loadAssets = async () => {
    try {
      const { data } = await instance.get('admin/assets');
      setAssets(data.data);
    } catch {
      toastError(`${t('failed_to_load')} ${t('assets')}`);
    }
  };

  const { initialValues, storedInitialValues, clear, create } =
    useLocalStorage<AccommodationValues>({
      initialValues: {
        name: '',
        description: '',
        address: '',
        floors: '',
        rooms: '',
        beds: '',
        section: '',
        asset_id: null,
        photos: [],
        assets: {
          id: 0,
          address: '',
          created_at: '',
          updated_at: '',
          name: '',
          inn: '',
          description: '',
          image_path: '',
          box_office: [],
        },
        timezone: {
          id: '',
          name: '',
        },
      },
      key: storageKey && `${storageKey}-v1`,
      exclude: [],
    });

  const formik = useFormik<AccommodationValues>({
    initialValues: storedInitialValues,

    validate,

    onSubmit: async values => {
      const updatedValues = {
        ...values,
        asset_id: values.assets.id,
        utc: values.timezone.id,
      };
      try {
        await onSubmit(updatedValues);
        clear();
        formik.resetForm({ values: initialValues });
        toastSuccess(t('accommodation_saved'));
      } catch (error) {
        toastError(error);
      }
    },
  });

  create(formik.values);

  const setValues = formik.setValues;

  useEffect(() => {
    if (!accommodation) return;

    const timeZone = timezonesList.find(item => item.id === accommodation.utc);

    setValues({
      assets: accommodation.assets,
      asset_id: accommodation.asset_id,
      name: accommodation.name,
      description: accommodation.description,
      address: accommodation.address,
      floors: accommodation.floors,
      rooms: accommodation.rooms,
      beds: accommodation.beds,
      photos: accommodation.photos || [],
      timezone: {
        id: timeZone?.id || '',
        name: timeZone?.name || '',
      },
      section: accommodation.section,
    });
  }, [accommodation, assets, setValues]);

  const onImagesChange = (image: IPhoto) => {
    const photoExists = formik.values.photos?.find(
      photo => photo.id === image.id
    );
    if (photoExists) {
      formik.setFieldValue(
        'photos',
        formik.values.photos.filter(photo => photo.id !== image.id)
      );
    } else {
      formik.setFieldValue('photos', [...formik.values.photos, image]);
    }
  };

  const handleRemoveImage = (id: number) => {
    formik.setFieldValue(
      'photos',
      formik.values.photos.filter(photo => photo.id !== id)
    );
  };

  const handleResetImages = () => {
    formik.setFieldValue('photos', []);
  };

  useEffect(() => {
    loadAssets();
  }, []);

  const debouncedName = useDebounce(formik.values.name, 500);
  const debouncedSection = useDebounce(formik.values.section, 500);

  const params = {
    page,
    perPage: 9,
    accommodation_name: `${debouncedName} ${debouncedSection}`,
  };

  return (
    <div className={styles.formWrapper}>
      <form
        className={styles.form}
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <div className={styles.formTop}>
          <InputBar
            name="name"
            value={formik.values.name}
            disabled={isLoading}
            label={t('name')}
            star={true}
            placeholder={t('item_name')}
            onChange={formik.handleChange}
            errors={formik.errors.name}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
          />

          {isAssetEditAvailable && (
            <CustomSelect
              placeholder={t('to_choose')}
              onChange={value => {
                formik.setFieldValue('assets', value);
              }}
              label={t('asset')}
              value={formik.values.assets}
              disabled={!isAssetEditAvailable || isLoading}
              options={assets}
              error={
                formik.touched.assets
                  ? (formik.errors.assets as string)
                  : undefined
              }
              onTouch={() => formik.setFieldTouched('assets', true)}
              required
            />
          )}
          <div className={styles.addressWrapper}>
            <InputBar
              name="address"
              value={formik.values.address}
              disabled={isLoading}
              label={t('address')}
              star={true}
              placeholder={t('address')}
              onChange={formik.handleChange}
              errors={formik.errors.address}
              onBlur={formik.handleBlur}
              error={formik.touched.address && Boolean(formik.errors.address)}
            />
            <InputBar
              name="section"
              value={formik.values.section ? formik.values.section : ''}
              disabled={isLoading}
              label={t('section')}
              star={false}
              placeholder={t('section')}
              onChange={formik.handleChange}
              errors={formik.errors.section}
              onBlur={formik.handleBlur}
              error={formik.touched.section && Boolean(formik.errors.address)}
            />
          </div>

          <Textarea
            showCounter
            rows={4}
            label={t('description')}
            placeholder={t('description')}
            maxLength={1000}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={() => formik.setFieldTouched('text', true)}
            name="description"
            star={false}
            disabled={isLoading}
          />

          <div className={styles.inputsWrapper}>
            <InputBar
              name="floors"
              value={formik.values.floors}
              disabled={isLoading}
              label={t('Floors')}
              star={false}
              placeholder={`${t('Quantity')} ${t('of')} ${t('floors')}`}
              onChange={formik.handleChange}
              errors={formik.errors.floors}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.floors)}
            />

            <InputBar
              name="rooms"
              value={formik.values.rooms}
              disabled={isLoading}
              label={t('Rooms')}
              star={false}
              placeholder={`${t('Quantity')} ${t('rooms')}`}
              onChange={formik.handleChange}
              errors={formik.errors.rooms}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.rooms)}
            />

            <InputBar
              name="beds"
              value={formik.values.beds}
              disabled={isLoading}
              label={t('Sleeping_places')}
              star={false}
              placeholder={`${t('Quantity')} ${t('sleeping_places')}`}
              onChange={formik.handleChange}
              errors={formik.errors.beds}
              onBlur={formik.handleBlur}
              error={Boolean(formik.errors.beds)}
            />
          </div>

          <div className={styles.imagesContainer}>
            <ul className={styles.imagesList}>
              {formik?.values?.photos?.map(image => (
                <div key={image.id} className={styles.imageWrapper}>
                  <img
                    className={styles.image}
                    src={image.full_photo_url}
                    alt="Announcement"
                  />
                  {hasAvailable && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(image.id)}
                      className={styles.deleteImageButton}
                    >
                      <CloseIcon className={styles.closeIcon} />
                    </button>
                  )}
                </div>
              ))}
            </ul>

            {hasAvailable && (
              <>
                <IconButton
                  onClick={() => setIsModalOpen(true)}
                  className={styles.pickPhotoButton}
                  isDisabled={!formik.values.name || !formik.values.section}
                >
                  <PhotoIcon />
                  <span className={styles.pickPhotoText}>
                    {t('choose_photo_for_accommodation')}
                  </span>
                </IconButton>
                {formik.errors.photos && (
                  <span className={styles.errorMsg}>
                    {formik.errors.photos as string}
                  </span>
                )}
              </>
            )}
          </div>

          <CustomSelect
            label={t('time_zone')}
            options={timezonesList}
            value={formik.values.timezone || ''}
            placeholder={t('timezone_validate')}
            onChange={value => {
              formik.setFieldValue('timezone', value);
            }}
            required
            error={formik.errors.timezone as string}
          />
        </div>

        <Button
          type="submit"
          text={t('save_n_exit')}
          className={styles.formBtn}
          disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}
        />
      </form>

      <Modal
        modalStyles={styles.photosModal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <PhotoPickerContainer
          queryKey={['accommodations-photos', params]}
          queryFn={() => getAccommodationsPhotos(params)}
          enabled={debouncedName !== '' && debouncedSection !== ''}
          initialPhotos={accommodation ? accommodation.photos : []}
          selectedImages={formik.values.photos || []}
          onImageChange={onImagesChange}
          onCloseClick={() => setIsModalOpen(false)}
          onResetClick={handleResetImages}
          changePage={setPage}
        />
      </Modal>
    </div>
  );
};
